import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation } from "react-router";
import { useEffect, type ReactNode } from "react";
import type { Route } from "./+types/root";
import { trackPageView } from "../src/utils/track";

// Threaded from infra/gnothi/web.ts, which reads the ONE canonical map rather than pasting the
// literal — the site id already lives in two files carrying an "edit both" comment and a third
// hand-written copy is how those drift. Absent on every non-prod stage (deliberate: that map has
// no non-prod entries), so the script tag simply isn't rendered there.
const ANALYTICS_SITE_ID = import.meta.env.VITE_ANALYTICS_SITE_ID;

const TITLE = "Gnothi — Stories and Audio Courses for Your Curiosity";
const DESCRIPTION = "Create a continuing story or audio course, then read or listen chapter by chapter. Notes, Fields, and shared context are available when you choose them.";

// Site-wide title/OG defaults — MUST be meta() not literal <head> tags: RR replaces the whole descriptor array with the leaf's meta(), so series*.tsx override cleanly; literals here would double-render title/og on the SEO pages. og:url stays per-route (series*.tsx) — one value misattributes.
// `robots: noindex,follow` rides that same wholesale replacement, deliberately: root's meta() is the ONLY head for `__spa-fallback.html` and for every SPA path (no `routes/spa/` module exports meta), so this is what stops a 3KB empty shell being indexed under the brand title on every non-existent path. Cost, accepted: /privacy, /terms, /disclaimer, /consumer-health-privacy, /auth and /app/* go noindex too — they already serve that same empty shell to a non-JS crawler, and the last two want noindex regardless. Every prerendered marketing page exports its own meta() and therefore carries NO robots tag; a page that stops exporting meta() silently goes noindex, which is the tripwire home.dom.test.tsx's siblings and the build-artifact grep exist to catch.
export const meta: Route.MetaFunction = () => [
  { title: TITLE },
  { name: "description", content: DESCRIPTION },
  { name: "robots", content: "noindex,follow" },
  { property: "og:title", content: TITLE },
  { property: "og:description", content: DESCRIPTION },
  { property: "og:image", content: "/og-image.jpg" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { property: "og:type", content: "website" },
];

// Framework mode owns the HTML document now (replaces index.html) — favicons/fonts/boot-scripts
// stay literal here; title/og live in meta() above so leaf routes can override them.
export function Layout({ children }: { children: ReactNode }) {
  return (
    // `suppressHydrationWarning`: the pre-paint script below adds `class="dark"` to THIS element before hydration, and the prerendered HTML carries no class — a guaranteed attribute mismatch for every dark-mode visitor to a prerendered page (React 19 #418). Unsuppressed, React does not merely warn: it patches the attribute back off, which is the dark→light flash the script exists to prevent. Scoped to this element's own attributes; child mismatches still report.
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32.png" />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#0A1128" />
        <meta name="fo-verify" content="247b505d-1f32-4c3d-8167-faf05a1e589e" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
        {/* `global`: Amplify bug https://github.com/aws-amplify/amplify-js/issues/678. `__zod_globalConfig`: Zod 4 otherwise probes `new Function` on its first parse and JIT-compiles parsers, which the CSP (no 'unsafe-eval', infra/gnothi/security-headers.ts) reports on every app load; it must be set before any module runs, and module chunks evaluate their imports first, so a `z.config()` call in app code lands too late. */}
        <script dangerouslySetInnerHTML={{ __html: "var global = global || window; globalThis.__zod_globalConfig = { jitless: true };" }} />
        {/* Apply MUI's persisted dark scheme before paint (matches Setup/Mui.tsx's colorSchemeSelector:'class';
            'mui-mode' is MUI's DEFAULT modeStorageKey, which Mui.tsx never overrides) so refreshing in dark doesn't flash light. */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "try { if (localStorage.getItem('mui-mode') === 'dark') document.documentElement.classList.add('dark'); } catch (e) {}",
          }}
        />
        {/* First-party analytics. `defer` (not `async`) so it executes ahead of <Scripts/> below
            and `window.gnothi` exists before hydration; `data-site` rather than a baked-in id, so
            the id has exactly one home. Served from this origin out of public/, built by
            infra/gnothi/web.ts at deploy time — 🔴 a MISSING /track.js does not 404 here. The
            CDN resolves every path in a viewer-request function, and a root-level key that misses
            both the file lookup and the build-dir prefixes falls through to the SPA fallback, so
            the browser gets __spa-fallback.html with a 200 and this <script> tag is fed HTML.
            The build gate in infra/gnothi/web.ts is what catches that (mechanism:
            infra/gnothi/spa-fallback.ts). */}
        {ANALYTICS_SITE_ID && <script defer data-site={ANALYTICS_SITE_ID} src="/track.js" />}
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Paints while the client-only SPA route modules (routes.ts `spa/`) load; also the only body of `__spa-fallback.html`. Empty on purpose: the prerendered pages never show it, and the SPA mounts its own loading state.
export function HydrateFallback() {
  return null;
}

export default function App() {
  // Every navigation, prerendered marketing route and legacy-SPA route alike — this is the one
  // component both mount under. The script (not this effect) owns WHEN the route shape is read:
  // it defers to the next macrotask and re-reads `location.pathname` at fire time, because
  // Setup/index.tsx's AuthRedirect bounces a logged-out visitor off /app/* mid-load and a shape read
  // here would attribute the wrong template. `location` (not `location.pathname`) is the dep so a
  // same-path replace still reports; the script de-duplicates consecutive identical shapes.
  const location = useLocation();
  useEffect(() => { trackPageView(); }, [location]);
  return <Outlet />;
}
